import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getSessionHeaders, getSessionIdFromJwt } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  RefreshCw,
  Database,
  Loader2,
  CheckCircle2,
  XCircle,
  CircleDashed,
  Pause,
  Play,
  Activity,
  ArrowUpRight,
  AlertTriangle,
  Compass,
  Plus,
  Plug,
  Pencil,
  ChevronDown,
  Table2,
  CircleCheck,
  CircleX,
  MoreVertical,
  Trash2,
  Wifi,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import AllConnectors from "@/components/admin/AllConnectors";
import ManualUploads from "@/components/admin/ManualUploads";
import GoogleDriveConnector from "@/components/admin/GoogleDriveConnector";
import GmailConnector from "@/components/admin/GmailConnector";
import ScriptsManager from "@/components/admin/ScriptsManager";
import DatabaseConnections from "@/components/admin/DatabaseConnections";
import GenericConnectorPage from "@/components/admin/GenericConnectorPage";
import ConnectFormRenderer, { type ConnectFormField, buildFormValues, extractConnectionParams } from "@/components/admin/ConnectFormRenderer";
import { ArrowLeft } from "lucide-react";

// --- Interfaces ---

interface PipelineStats {
  SinkedOK?: number;
  RetryTotal?: number;
  SinkedFail?: number;
  DroppedTotal?: number;
  SourcedTotal?: number;
  TransformedOK?: number;
  TransformedFail?: number;
}

interface SyncingJob {
  id: string;
  name: string;
  connection_id: string | null;
  job_name: string | null;
  connection_name: string | null;
  source_table_name: string | null;
  dest_table_name: string | null;
  columns: unknown;
  offset_column: string | null;
  connect_type: string | null;
  last_sync_start: string | null;
  last_sync_end: string | null;
  last_sync_status: string | null;
  logo: string | null;
  logo_color: string | null;
  no_of_records: number | null;
  paused: boolean | null;
  last_offset: string | null;
  last_stats: PipelineStats | null;
  created_at: string;
  updated_at: string;
}

interface LastExecution {
  id: string;
  source_syncing_job_id: string;
  last_run_start_time: string | null;
  last_run_end_time: string | null;
  status: string;
  no_of_records: number;
  log: string | null;
  created_at: string;
}

interface ConnectionParams {
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  password?: string;
  connection_string?: string;
  ssl_enabled?: boolean;
}

interface SourceConnector {
  id: string;
  name: string;
  db_type: string;
  connection_params: ConnectionParams;
  enabled: boolean;
  logo: string | null;
  logo_color: string | null;
  created_at: string;
  syncing_jobs?: SyncingJob[];
  connect_type: string;
}

interface ColumnInfo {
  column_name: string;
  data_type: string;
  is_nullable: string;
}

interface TableSchema {
  table_name: string;
  columns: ColumnInfo[];
}

interface SelectedSchema {
  [tableName: string]: string[];
}

type ConnectionStatus = "idle" | "checking" | "connected" | "failed";

interface StatusInfo {
  status: ConnectionStatus;
  message?: string;
  latency_ms?: number;
}

// --- Constants ---

const DB_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  postgres: { label: "PostgreSQL", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  mysql: { label: "MySQL", color: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  clickhouse: { label: "ClickHouse", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  bigquery: { label: "BigQuery", color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" },
  mongodb: { label: "MongoDB", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
};

// Fallback logo/color for known db_types when logo field is null
const DB_TYPE_LOGO: Record<string, { logo: string; color: string }> = {
  postgres: { logo: "postgresql", color: "#336791" },
  mysql: { logo: "mysql", color: "#4479A1" },
  clickhouse: { logo: "clickhouse", color: "#FFCC00" },
  bigquery: { logo: "googlebigquery", color: "#669DF6" },
  mongodb: { logo: "mongodb", color: "#47A248" },
};

const statusConfig: Record<
  string,
  { icon: typeof CheckCircle2; label: string; color: string; bg: string; pulse?: boolean }
> = {
  completed: {
    icon: CheckCircle2,
    label: "SYNCED",
    color: "text-emerald-400",
    bg: "bg-emerald-400/10 border-emerald-400/20",
  },
  success: {
    icon: CheckCircle2,
    label: "SYNCED",
    color: "text-emerald-400",
    bg: "bg-emerald-400/10 border-emerald-400/20",
  },
  running: {
    icon: Loader2,
    label: "RUNNING",
    color: "text-blue-400",
    bg: "bg-blue-400/10 border-blue-400/20",
    pulse: true,
  },
  failed: { icon: XCircle, label: "FAILED", color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
  error: { icon: AlertTriangle, label: "ERROR", color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
  pending: {
    icon: CircleDashed,
    label: "PENDING",
    color: "text-yellow-400",
    bg: "bg-yellow-400/10 border-yellow-400/20",
  },
};

// --- Small components ---

const StatusIndicator = ({ status }: { status: string }) => {
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded border px-2 py-0.5 font-mono text-[11px] font-semibold tracking-wider",
        config.bg,
        config.color,
      )}
    >
      <Icon className={cn("h-3 w-3", status === "running" && "animate-spin", config.pulse && "animate-pulse")} />
      {config.label}
    </span>
  );
};

const PipelineLogo = ({
  logo,
  logoColor,
  size = "sm",
}: {
  logo: string | null;
  logoColor: string | null;
  size?: "sm" | "md";
}) => {
  const [imgError, setImgError] = useState(false);
  const dim = size === "md" ? "h-8 w-8" : "h-6 w-6";
  const iconDim = size === "md" ? 18 : 14;

  if (logo && !imgError) {
    return (
      <div
        className={cn("flex shrink-0 items-center justify-center rounded-md overflow-hidden", dim)}
        style={{ backgroundColor: logoColor || "#6366f1" }}
      >
        <img
          src={`https://cdn.simpleicons.org/${logo}/white`}
          alt=""
          style={{ width: iconDim, height: iconDim }}
          className="object-contain"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={cn("flex shrink-0 items-center justify-center rounded-md", dim)}
      style={{ backgroundColor: logoColor || "#6366f1" }}
    >
      <Database className="text-white" style={{ width: iconDim, height: iconDim }} />
    </div>
  );
};

// --- Connector items for popup routing ---
const CONNECTOR_ITEMS: { id: string; label: string; dbType: string | null }[] = [
  { id: "ds-sql-db", label: "Databases", dbType: null },
  { id: "ds-scripts", label: "Scripts", dbType: null },
  { id: "ds-manual-uploads", label: "Manual Uploads", dbType: null },
  { id: "ds-gmail", label: "Gmail", dbType: null },
  { id: "ds-google-drive", label: "Google Drive", dbType: null },
  { id: "ds-rest-apis", label: "Rest APIs", dbType: "rest_api" },
  { id: "ds-webhooks", label: "Web hooks", dbType: "webhook" },
  { id: "ds-frontend-sdks", label: "Frontend SDKs", dbType: "frontend_sdk" },
  { id: "sl-facebook", label: "Facebook", dbType: "facebook" },
  { id: "sl-instagram", label: "Instagram", dbType: "instagram" },
  { id: "sl-google-reviews", label: "Google Reviews", dbType: "google_reviews" },
  { id: "sl-playstore", label: "Playstore", dbType: "playstore" },
  { id: "sl-appstore", label: "Appstore", dbType: "appstore" },
  { id: "oc-freshdesk", label: "Freshdesk", dbType: "freshdesk" },
  { id: "oc-zendesk", label: "Zendesk", dbType: "zendesk" },
  { id: "oc-salesforce", label: "Salesforce", dbType: "salesforce" },
  { id: "oc-zoho", label: "Zoho", dbType: "zoho" },
  { id: "oc-shopify", label: "Shopify", dbType: "shopify" },
  { id: "mk-webengage", label: "WebEngage", dbType: "webengage" },
  { id: "mk-moengage", label: "MoEngage", dbType: "moengage" },
  { id: "mk-clevertap", label: "CleverTap", dbType: "clevertap" },
];

// --- Main component ---

const SyncingMonitor = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<SyncingJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [lastExecs, setLastExecs] = useState<Record<string, LastExecution>>({});
  const [connectors, setConnectors] = useState<SourceConnector[]>([]);
  const [connectorsLoading, setConnectorsLoading] = useState(true);
  const [allConnectorsOpen, setAllConnectorsOpen] = useState(false);
  const [popupConnector, setPopupConnector] = useState<string | null>(null);

  // Connector dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConnector, setDialogConnector] = useState<SourceConnector | null>(null);
  const [dialogTab, setDialogTab] = useState<"tables" | "details">("tables");
  const [saving, setSaving] = useState(false);
  const [connStatus, setConnStatus] = useState<StatusInfo>({ status: "idle" });
  const [editMode, setEditMode] = useState(false);

  // Schema/table selection state
  const [schema, setSchema] = useState<TableSchema[]>([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [selections, setSelections] = useState<SelectedSchema>({});
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [offsetColumns, setOffsetColumns] = useState<Record<string, string>>({});
  const [columnDescriptions, setColumnDescriptions] = useState<Record<string, Record<string, string>>>({});
  const [savingSchema, setSavingSchema] = useState<string | null>(null);
  const [allowRemote, setAllowRemote] = useState(false);
  const [connectTypes, setConnectTypes] = useState<Record<string, string>>({});

  // Add new connector dialog state
  const [addConnOpen, setAddConnOpen] = useState(false);
  const [addConnDbType, setAddConnDbType] = useState("");
  const [addConnSaving, setAddConnSaving] = useState(false);

  // Dynamic connect form from all_connectors
  const [connectFormFields, setConnectFormFields] = useState<ConnectFormField[] | null>(null);
  const [dynamicFormValues, setDynamicFormValues] = useState<Record<string, unknown>>({});

  // --- Data fetching ---

  const fetchJobs = async () => {
    if (!profile?.client_id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("pipeline_jobs" as any)
      .select("*")
      .eq("client_id", profile.client_id)
      .order("created_at", { ascending: false });
    const pipelines = (data as unknown as SyncingJob[]) || [];
    if (!error) setJobs(pipelines);

    if (pipelines.length > 0) {
      const ids = pipelines.map((j) => j.id);
      const { data: historyData } = await supabase
        .from("pipelinejob_checkpoints" as any)
        .select("*")
        .in("source_syncing_job_id", ids)
        .order("created_at", { ascending: false });
      if (historyData) {
        const execs: Record<string, LastExecution> = {};
        (historyData as unknown as LastExecution[]).forEach((h) => {
          if (!execs[h.source_syncing_job_id]) {
            execs[h.source_syncing_job_id] = h;
          }
        });
        setLastExecs(execs);
      }
    }

    setLoading(false);
    setLastRefresh(new Date());
  };

  const fetchConnectors = async () => {
    if (!profile?.client_id) return;
    setConnectorsLoading(true);
    const { data } = await supabase
      .from("pipeline_source_connector")
      .select("id, name, db_type, connection_params, enabled, logo, logo_color, created_at, connect_type")
      .eq("client_id", profile.client_id)
      .order("created_at", { ascending: false });
    if (data) {
      const conns = (data as unknown as SourceConnector[]) || [];
      // Attach syncing jobs
      const { data: allJobs } = await supabase.from("pipeline_jobs" as any).select("*").eq("client_id", profile.client_id);
      if (allJobs) {
        const jobsByConn: Record<string, SyncingJob[]> = {};
        (allJobs as unknown as SyncingJob[]).forEach((j) => {
          const connId = j.connection_id || "";
          if (!jobsByConn[connId]) jobsByConn[connId] = [];
          jobsByConn[connId].push(j);
        });
        conns.forEach((c) => {
          c.syncing_jobs = jobsByConn[c.id] || [];
        });
      }
      setConnectors(conns);
    }
    setConnectorsLoading(false);
  };

  useEffect(() => {
    fetchJobs();
    fetchConnectors();
  }, [profile?.client_id]);

  // --- Add new connector ---

  const openAddConnector = async (dbType: string) => {
    setAddConnDbType(dbType);
    setAddConnOpen(true);
    // Fetch connect_form from all_connectors
    const { data } = await supabase
      .from("all_connectors")
      .select("connect_form")
      .eq("db_type", dbType)
      .limit(1)
      .maybeSingle();
    const fields = (data as any)?.connect_form as ConnectFormField[] | null;
    setConnectFormFields(fields);
    setDynamicFormValues(fields ? buildFormValues(fields) : { name: "", connection_string: "" });
  };

  const handleAddNewConnector = async () => {
    const connName = (dynamicFormValues.name as string || "").trim();
    if (!profile?.client_id || !connName) {
      toast.error("Connection name is required");
      return;
    }
    setAddConnSaving(true);
    const connectionParams = connectFormFields
      ? extractConnectionParams(connectFormFields, dynamicFormValues)
      : {};
    const dbLogo = DB_TYPE_LOGO[addConnDbType];
    const { error } = await supabase.from("pipeline_source_connector").insert({
      client_id: profile.client_id,
      name: connName,
      db_type: addConnDbType,
      connection_params: connectionParams,
      enabled: false,
      logo: dbLogo?.logo || null,
      logo_color: dbLogo?.color || null,
    } as any);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Connector added (disabled by default)");
      setAddConnOpen(false);
      fetchConnectors();
    }
    setAddConnSaving(false);
  };

  const handleDeleteConnector = async (e: React.MouseEvent, connector: SourceConnector) => {
    e.stopPropagation();
    if ((connector.syncing_jobs?.length ?? 0) > 0) {
      toast.error("Cannot delete connector with active pipelines. Remove all pipelines first.");
      return;
    }
    const { error } = await supabase
      .from("pipeline_source_connector")
      .delete()
      .eq("id", connector.id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Connector deleted");
      fetchConnectors();
    }
  };

  // --- Pipeline actions ---

  const togglePause = async (e: React.MouseEvent, job: SyncingJob) => {
    e.stopPropagation();
    const newPaused = !job.paused;
    const { error } = await supabase
      .from("pipeline_jobs" as any)
      .update({ paused: newPaused } as any)
      .eq("id", job.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, paused: newPaused } : j)));
    toast.success(newPaused ? "Pipeline paused" : "Pipeline resumed");
  };

  // --- Connector dialog actions ---

  const openConnectorDialog = async (connector: SourceConnector) => {
    setDialogConnector(connector);
    const params = connector.connection_params || {};
    setConnStatus({ status: "idle" });
    setEditMode(false);
    setDialogTab("tables");
    setSchema([]);
    setSelections({});
    setExpandedTables(new Set());
    setOffsetColumns({});
    setColumnDescriptions({});
    setConnectTypes({});
    setAllowRemote(false);
    setDialogOpen(true);
    // Auto-fetch schema since tables is the default tab
    fetchSchema(connector);
    // Fetch connect_form and allow_remote from all_connectors
    if (connector.db_type) {
      supabase
        .from("all_connectors")
        .select("allow_remote, connect_form")
        .eq("db_type", connector.db_type)
        .limit(1)
        .then(({ data }) => {
          if (data && data.length > 0) {
            setAllowRemote(!!(data[0] as any).allow_remote);
            const fields = (data[0] as any).connect_form as ConnectFormField[] | null;
            setConnectFormFields(fields);
            if (fields) {
              setDynamicFormValues(buildFormValues(fields, params as Record<string, unknown>, connector.name));
            }
          }
        });
    }
  };

  const testConnection = async (connId: string) => {
    setConnStatus({ status: "checking" });
    try {
      const { data, error } = await supabase.functions.invoke("db-test-connection", {
        headers: await getSessionHeaders(),
        body: { connection_id: connId },
      });
      if (error) throw error;
      if (data?.ok) {
        setConnStatus({ status: "connected", message: data.message, latency_ms: data.latency_ms });
      } else {
        setConnStatus({ status: "failed", message: data?.message || "Connection failed" });
      }
    } catch (e: any) {
      setConnStatus({ status: "failed", message: e.message || "Connection failed" });
    }
  };

  const handleSaveConnection = async () => {
    if (!dialogConnector) return;

    const connName = (dynamicFormValues.name as string || "").trim();

    if (!connName) {
      toast.error("Connection name is required");
      return;
    }
    if (!connectFormFields) {
      toast.error("Form not loaded yet");
      return;
    }
    setSaving(true);

    const connectionParams = extractConnectionParams(connectFormFields, dynamicFormValues);
    // Preserve existing password if not provided
    if (!dynamicFormValues.password && dialogConnector.connection_params?.password) {
      connectionParams.password = dialogConnector.connection_params.password;
    }

    const { error } = await supabase
      .from("pipeline_source_connector")
      .update({
        name: connName,
        db_type: dialogConnector.db_type,
        connection_params: connectionParams,
      } as any)
      .eq("id", dialogConnector.id);

    if (error) toast.error(error.message);
    else {
      toast.success("Connection updated");
      fetchConnectors();
    }
    setSaving(false);
  };

  const toggleEnabled = async (id: string, enabled: boolean) => {
    const { error } = await supabase
      .from("pipeline_source_connector")
      .update({ enabled } as any)
      .eq("id", id);
    if (error) toast.error(error.message);
    else {
      setConnectors((c) => c.map((x) => (x.id === id ? { ...x, enabled } : x)));
      if (dialogConnector?.id === id) setDialogConnector((prev) => (prev ? { ...prev, enabled } : prev));
    }
  };

  // --- Schema fetching & table selection ---

  const fetchSchema = async (conn: SourceConnector) => {
    // Build selections from existing syncing_jobs
    const sel: SelectedSchema = {};
    const offsets: Record<string, string> = {};
    const descs: Record<string, Record<string, string>> = {};
    const cTypes: Record<string, string> = {};
    (conn.syncing_jobs || []).forEach((j) => {
      if (!j.source_table_name) return;
      const cols = Array.isArray(j.columns) ? (j.columns as any[]).map((c: any) => c.column_name || c) : [];
      sel[j.source_table_name] = cols;
      if (j.offset_column) offsets[j.source_table_name] = j.offset_column;
      if (j.connect_type) cTypes[j.source_table_name] = j.connect_type;
      if (Array.isArray(j.columns)) {
        const tableDescs: Record<string, string> = {};
        (j.columns as any[]).forEach((c: any) => {
          if (c.description) tableDescs[c.column_name || c] = c.description;
        });
        if (Object.keys(tableDescs).length > 0) descs[j.source_table_name] = tableDescs;
      }
    });
    setSelections(sel);
    setOffsetColumns(offsets);
    setConnectTypes(cTypes);
    setColumnDescriptions(descs);
    const existingOffsets = { ...offsets };
    setExpandedTables(new Set());
    setSchemaLoading(true);
    setSchema([]);

    try {
      const { data, error } = await supabase.functions.invoke("db-schema", {
        headers: await getSessionHeaders(),
        body: { connection_id: conn.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const fetchedSchema: TableSchema[] = data.schema || [];
      setSchema(fetchedSchema);
      // Auto-set offset columns
      const autoOffsets = { ...existingOffsets };
      fetchedSchema.forEach((t) => {
        if (!autoOffsets[t.table_name]) {
          const updatedAtCol = t.columns.find((c) => c.column_name === "updated_at" || c.column_name === "update_at");
          if (updatedAtCol) {
            autoOffsets[t.table_name] = updatedAtCol.column_name;
          } else {
            const createdAtCol = t.columns.find((c) => c.column_name === "created_at");
            if (createdAtCol) {
              autoOffsets[t.table_name] = createdAtCol.column_name;
            } else {
              const idCol = t.columns.find((c) => c.column_name === "id");
              if (idCol) autoOffsets[t.table_name] = idCol.column_name;
            }
          }
        }
      });
      setOffsetColumns(autoOffsets);
    } catch (e: any) {
      toast.error(e.message || "Failed to fetch schema");
    } finally {
      setSchemaLoading(false);
    }
  };

  const switchToTablesTab = () => {
    if (dialogConnector) {
      setDialogTab("tables");
      if (schema.length === 0 && !schemaLoading) {
        fetchSchema(dialogConnector);
      }
    }
  };

  const isTableSelected = (tableName: string) => !!selections[tableName];
  const isColumnSelected = (tableName: string, colName: string) => selections[tableName]?.includes(colName) ?? false;

  const toggleTable = (tableName: string, columns: ColumnInfo[]) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (next[tableName]) delete next[tableName];
      else next[tableName] = columns.map((c) => c.column_name);
      return next;
    });
  };

  const toggleColumn = (tableName: string, colName: string) => {
    setSelections((prev) => {
      const next = { ...prev };
      const current = next[tableName] || [];
      if (current.includes(colName)) {
        const updated = current.filter((c) => c !== colName);
        if (updated.length === 0) delete next[tableName];
        else next[tableName] = updated;
      } else {
        next[tableName] = [...current, colName];
      }
      return next;
    });
  };

  const selectAll = () => {
    const all: SelectedSchema = {};
    schema.forEach((t) => {
      all[t.table_name] = t.columns.map((c) => c.column_name);
    });
    setSelections(all);
  };

  const deselectAll = () => setSelections({});

  const allSelected =
    schema.length > 0 &&
    schema.every((t) => !!selections[t.table_name] && selections[t.table_name].length === t.columns.length);

  const toggleExpandTable = (tableName: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tableName)) next.delete(tableName);
      else next.add(tableName);
      return next;
    });
  };

  const scheduleSyncForTable = async (tableName: string) => {
    if (!dialogConnector || !profile?.client_id) return;
    const cols = selections[tableName];
    if (!cols || cols.length === 0) {
      toast.error("Select at least one column first");
      return;
    }
    const offset = offsetColumns[tableName];
    if (!offset) {
      toast.error("Please select an offset column before scheduling sync");
      return;
    }
    setSavingSchema(tableName);
    try {
      await supabase
        .from("pipeline_jobs" as any)
        .delete()
        .eq("connection_id", dialogConnector.id)
        .eq("source_table_name", tableName);

      const tableSchema = schema.find((t) => t.table_name === tableName);
      const tableDescs = columnDescriptions[tableName] || {};
      const columnsJson = cols.map((colName) => {
        const colInfo = tableSchema?.columns.find((c) => c.column_name === colName);
        return {
          column_name: colName,
          data_type: colInfo?.data_type || "text",
          is_nullable: colInfo?.is_nullable || "YES",
          ...(tableDescs[colName] ? { description: tableDescs[colName] } : {}),
        };
      });

      const { data: sessionData } = await supabase.auth.getSession();
      const sessionId = getSessionIdFromJwt(sessionData.session?.access_token);

      const connectType = connectTypes[tableName] || "sync";
      const { error } = await supabase.from("pipeline_jobs" as any).insert({
        connection_id: dialogConnector.id,
        client_id: profile.client_id,
        name: `${dialogConnector.name}_${tableName}`,
        job_name: `${profile.client_id}_${dialogConnector.name}_${tableName}`,
        connection_name: dialogConnector.name,
        source_table_name: tableName,
        columns: columnsJson,
        offset_column: offset,
        connect_type: connectType,
        logo: dialogConnector.logo || null,
        logo_color: dialogConnector.logo_color || null,
        ...(sessionId ? { session_id: sessionId } : {}),
      } as any);
      if (error) throw error;

      toast.success(`Sync scheduled for ${tableName}`);
      // Refresh both jobs and connectors
      fetchJobs();
      const { data: updatedJobs } = await supabase.from("pipeline_jobs" as any).select("*").eq("connection_id", dialogConnector.id);
      const newJobs = (updatedJobs as unknown as SyncingJob[]) || [];
      setConnectors((c) => c.map((x) => (x.id === dialogConnector.id ? { ...x, syncing_jobs: newJobs } : x)));
      setDialogConnector((prev) => (prev ? { ...prev, syncing_jobs: newJobs } : prev));
    } catch (e: any) {
      toast.error(e.message || "Failed to schedule sync");
    }
    setSavingSchema(null);
  };

  // --- Computed values ---

  const totalRecords = jobs.reduce((s, j) => s + (j.no_of_records || 0), 0);
  const activeCount = jobs.filter((j) => !j.paused).length;
  const failedCount = jobs.filter((j) => j.last_sync_status === "failed" || j.last_sync_status === "error").length;
  const runningCount = jobs.filter((j) => j.last_sync_status === "running").length;

  const connectorsByType = useMemo(() => {
    const grouped: Record<string, SourceConnector[]> = {};
    connectors.forEach((c) => {
      if (!grouped[c.db_type]) grouped[c.db_type] = [];
      grouped[c.db_type].push(c);
    });
    return grouped;
  }, [connectors]);

  const hasPipelines = jobs.length > 0;
  const hasConnectors = connectors.length > 0;
  const selectedCount = Object.keys(selections).length;
  const totalColumnsSelected = Object.values(selections).reduce((sum, cols) => sum + cols.length, 0);

  // --- Render ---

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 px-6 py-4 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="font-mono text-lg font-bold tracking-tight">SYNC MONITOR</h1>
              <p className="text-sm text-muted-foreground">Syncing to your private Databull AI data cluster</p>
              <p className="font-mono text-[11px] text-muted-foreground">
                {jobs.length} pipeline{jobs.length !== 1 ? "s" : ""} · {connectors.length} source connector
                {connectors.length !== 1 ? "s" : ""} · last refresh{" "}
                {formatDistanceToNow(lastRefresh, { addSuffix: true })}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchJobs();
              fetchConnectors();
            }}
            disabled={loading}
            className="font-mono text-xs gap-1.5"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            REFRESH
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      {hasPipelines && (
        <div className="border-b border-border bg-card/30 px-6 py-3 shrink-0">
          <div className="flex items-center gap-6 font-mono text-xs">
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">TOTAL:</span>
              <span className="font-semibold">{jobs.length}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="text-muted-foreground">ACTIVE:</span>
              <span className="font-semibold text-emerald-400">{activeCount}</span>
            </div>
            {runningCount > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
                <span className="text-muted-foreground">RUNNING:</span>
                <span className="font-semibold text-blue-400">{runningCount}</span>
              </div>
            )}
            {failedCount > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-red-400" />
                <span className="text-muted-foreground">FAILED:</span>
                <span className="font-semibold text-red-400">{failedCount}</span>
              </div>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <span className="text-muted-foreground">RECORDS:</span>
              <span className="font-semibold tabular-nums">{totalRecords.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Split Layout */}
      <div className="flex flex-col lg:flex-row gap-0 lg:gap-0 min-h-0 flex-1">
        {/* Left Side - Pipeline / Sync Monitor */}
        {hasPipelines && (
          <div className={cn("flex-1 min-w-0 flex flex-col overflow-hidden", hasConnectors && "lg:border-r lg:border-border")}>
            <div className="px-4 py-3 border-b border-border bg-card/20 shrink-0">
              <h2 className="font-mono text-xs font-semibold tracking-wider text-muted-foreground flex items-center gap-2">
                <Activity className="h-3.5 w-3.5" />
                PIPELINES
              </h2>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground gap-2 font-mono text-sm">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Loading pipelines...
                </div>
              ) : (
                jobs.map((job) => {
                  const colCount = Array.isArray(job.columns) ? job.columns.length : 0;
                  const lastExec = lastExecs[job.id];
                  return (
                    <div
                      key={job.id}
                      onClick={() => navigate(`/sync-monitor/${job.id}`)}
                      className={cn(
                        "group relative cursor-pointer rounded-lg border border-border bg-card transition-all hover:border-primary/40 hover:shadow-md",
                        job.paused && "opacity-60",
                      )}
                    >
                      <div className="flex items-center gap-4 px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <PipelineLogo
                            logo={job.logo || DB_TYPE_LOGO[job.connect_type || ""]?.logo || null}
                            logoColor={job.logo_color || DB_TYPE_LOGO[job.connect_type || ""]?.color || null}
                          />
                          <StatusIndicator status={job.paused ? "pending" : (job.last_sync_status || "pending")} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-semibold truncate">
                                {(job.job_name || job.name || "—").split("_").slice(1).join("_") || job.job_name || job.name || "—"}
                              </span>
                              <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 font-mono text-[11px] text-muted-foreground">
                              <span>{job.connection_name || "—"}</span>
                              {lastExec && (
                                <>
                                  <span className="text-border">·</span>
                                  <span
                                    className={cn(
                                      (lastExec.status === "completed" || lastExec.status === "success") &&
                                        "text-emerald-400/70",
                                      (lastExec.status === "failed" || lastExec.status === "error") &&
                                        "text-red-400/70",
                                      lastExec.status === "running" && "text-blue-400/70",
                                    )}
                                  >
                                    {lastExec.status}
                                  </span>
                                  {lastExec.no_of_records > 0 && (
                                    <>
                                      <span className="text-border">·</span>
                                      <span className="tabular-nums">
                                        {lastExec.no_of_records.toLocaleString()} rows
                                      </span>
                                    </>
                                  )}
                                  {lastExec.last_run_end_time && (
                                    <>
                                      <span className="text-border">·</span>
                                      <span>
                                        {formatDistanceToNow(new Date(lastExec.last_run_end_time), { addSuffix: true })}
                                      </span>
                                    </>
                                  )}
                                  {lastExec.log && (
                                    <>
                                      <span className="text-border">·</span>
                                      <span className="truncate max-w-[200px]" title={lastExec.log}>
                                        {lastExec.log}
                                      </span>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="hidden xl:flex items-center gap-2 text-xs font-mono shrink-0">
                          <code className="rounded bg-muted/50 border border-border px-2 py-0.5 text-[11px]">
                            {job.source_table_name || "—"}
                          </code>
                          <span className="text-muted-foreground">→</span>
                          <code className="rounded bg-muted/50 border border-border px-2 py-0.5 text-[11px]">
                            {job.dest_table_name || "auto"}
                          </code>
                        </div>

                        {job.last_stats && (
                          <div className="hidden md:flex items-center gap-3 font-mono text-[11px] text-muted-foreground shrink-0">
                            <div className="text-right">
                              <div className="text-foreground font-semibold tabular-nums">
                                {(job.last_stats.SourcedTotal ?? 0).toLocaleString()}
                              </div>
                              <div className="text-[10px]">sourced</div>
                            </div>
                            <div className="text-right">
                              <div className="text-emerald-400 font-semibold tabular-nums">
                                {(job.last_stats.SinkedOK ?? 0).toLocaleString()}
                              </div>
                              <div className="text-[10px]">sinked</div>
                            </div>
                            {(job.last_stats.SinkedFail ?? 0) > 0 && (
                              <div className="text-right">
                                <div className="text-red-400 font-semibold tabular-nums">
                                  {(job.last_stats.SinkedFail ?? 0).toLocaleString()}
                                </div>
                                <div className="text-[10px]">failed</div>
                              </div>
                            )}
                            {(job.last_stats.DroppedTotal ?? 0) > 0 && (
                              <div className="text-right">
                                <div className="text-yellow-400 font-semibold tabular-nums">
                                  {(job.last_stats.DroppedTotal ?? 0).toLocaleString()}
                                </div>
                                <div className="text-[10px]">dropped</div>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="hidden xl:block font-mono text-[11px] text-muted-foreground text-right shrink-0 w-[130px]">
                          {job.last_sync_end ? (
                            <>
                              <div className="text-foreground">{format(new Date(job.last_sync_end), "HH:mm:ss")}</div>
                              <div className="text-[10px]">{format(new Date(job.last_sync_end), "MMM d, yyyy")}</div>
                            </>
                          ) : (
                            <span className="text-muted-foreground/50">never synced</span>
                          )}
                        </div>

                        <button
                          onClick={(e) => togglePause(e, job)}
                          className={cn(
                            "flex h-8 w-8 items-center justify-center rounded-md border transition-colors shrink-0",
                            job.paused
                              ? "border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/10"
                              : "border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/10",
                          )}
                          title={job.paused ? "Resume pipeline" : "Pause pipeline"}
                        >
                          {job.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                        </button>
                      </div>

                      {job.last_sync_status === "running" && !job.paused && (
                        <div className="h-0.5 bg-blue-400/20 overflow-hidden rounded-b-lg">
                          <div className="h-full w-1/3 bg-blue-400 animate-pulse rounded-full" />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Right Side - Pipeline Source Connectors */}
        <div className={cn("flex flex-col overflow-hidden", hasPipelines ? "lg:w-[380px] lg:shrink-0" : "flex-1")}>
          <div className="px-4 py-3 border-b border-border bg-card/20 shrink-0 flex items-center justify-between">
            <h2 className="font-mono text-xs font-semibold tracking-wider text-muted-foreground flex items-center gap-2">
              <Plug className="h-3.5 w-3.5" />
              DATA CONNECTORS
            </h2>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate("/data-connectors?connector=all-connectors")}>
              Explore more
            </Button>
          </div>

          {connectorsLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground gap-2 font-mono text-sm">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading connectors...
            </div>
          ) : !hasConnectors ? (
            <div className="flex flex-col items-center justify-center flex-1 px-6 gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 border border-primary/20">
                <Database className="h-7 w-7 text-primary" />
              </div>
              <div className="text-center space-y-1">
                <p className="font-mono text-sm font-semibold">Build your data stream cluster</p>
                <p className="text-muted-foreground text-sm">Choose your data sources to get started</p>
              </div>
              <Button onClick={() => setAllConnectorsOpen(true)} className="gap-2 mt-2">
                <Compass className="h-4 w-4" />
                Explore Data Sources
              </Button>
            </div>
          ) : (
            <div className="flex flex-col flex-1 overflow-hidden">
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {Object.entries(connectorsByType).map(([dbType, items]) => {
                const typeInfo = DB_TYPE_LABELS[dbType] || {
                  label: dbType,
                  color: "bg-muted text-muted-foreground border-border",
                };
                return (
                  <div key={dbType}>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className={cn("font-mono text-[10px] tracking-wider", typeInfo.color)}>
                        {typeInfo.label}
                      </Badge>
                      <span className="font-mono text-[10px] text-muted-foreground">{items.length}</span>
                      <button
                        onClick={() => openAddConnector(dbType)}
                        className="ml-auto flex h-5 w-5 items-center justify-center rounded border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground hover:border-primary/30"
                        title={`Add new ${typeInfo.label} connector`}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((connector) => {
                        const dbLogo = DB_TYPE_LOGO[connector.db_type];
                        const effectiveLogo = connector.logo || dbLogo?.logo || null;
                        const effectiveColor = connector.logo_color || dbLogo?.color || null;
                        return (
                          <div
                            key={connector.id}
                            onClick={() => openConnectorDialog(connector)}
                            className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 transition-colors hover:border-primary/30 cursor-pointer"
                          >
                            <PipelineLogo logo={effectiveLogo} logoColor={effectiveColor} size="sm" />
                            <span className="font-mono text-sm truncate flex-1">{connector.name}</span>
                            {connector.connect_type === "remote" ? (
                              <Badge variant="outline" className="font-mono text-[9px] px-1.5 py-0 shrink-0 border-blue-500/30 text-blue-400">
                                <Wifi className="h-2.5 w-2.5 mr-0.5" />
                                Remote MCP
                              </Badge>
                            ) : (connector.syncing_jobs?.length ?? 0) > 0 ? (
                              <Badge variant="outline" className="font-mono text-[9px] px-1.5 py-0 shrink-0 border-emerald-500/30 text-emerald-400">
                                <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                                {connector.syncing_jobs!.length} synced
                              </Badge>
                            ) : null}
                            <span
                              className={cn(
                                "h-2 w-2 rounded-full shrink-0",
                                connector.enabled ? "bg-emerald-400" : "bg-muted-foreground/30",
                              )}
                            />
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <button className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground shrink-0">
                                  <MoreVertical className="h-3.5 w-3.5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => handleDeleteConnector(e, connector)}
                                  disabled={(connector.syncing_jobs?.length ?? 0) > 0}
                                  className={cn(
                                    (connector.syncing_jobs?.length ?? 0) > 0
                                      ? "text-muted-foreground cursor-not-allowed"
                                      : "text-destructive focus:text-destructive"
                                  )}
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                                  {(connector.syncing_jobs?.length ?? 0) > 0 ? "Has pipelines" : "Delete"}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

            </div>
            {/* Explore more data sources button - sticky bottom */}
            <div className="shrink-0 border-t border-border bg-card/50 p-3">
              <Button className="w-full gap-2 font-mono text-xs" onClick={() => setAllConnectorsOpen(true)}>
                <Compass className="h-3.5 w-3.5" />
                Explore more data sources
              </Button>
            </div>
            </div>
          )}
        </div>
      </div>

      {/* Connector Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Database className="h-5 w-5" />
              <span>{dialogConnector?.name}</span>
              {dialogConnector && (
                <Badge
                  variant="outline"
                  className={cn("font-mono text-[10px]", DB_TYPE_LABELS[dialogConnector.db_type]?.color)}
                >
                  {DB_TYPE_LABELS[dialogConnector.db_type]?.label || dialogConnector.db_type}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Tab switcher */}
          <div className="flex gap-1 border-b border-border pb-0">
            <button
              onClick={switchToTablesTab}
              className={cn(
                "px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px",
                dialogTab === "tables"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              Tables
            </button>
            <button
              onClick={() => setDialogTab("details")}
              className={cn(
                "px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px",
                dialogTab === "details"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              Connection Details
            </button>
          </div>

          <div className="flex-1 overflow-auto">
            {/* Connection Details Tab */}
            {dialogTab === "details" && dialogConnector && (
              <div className="space-y-4 py-2">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">Connector Enabled</p>
                    <p className="text-xs text-muted-foreground">
                      {dialogConnector.enabled ? "This connector is active" : "This connector is disabled"}
                    </p>
                  </div>
                  <Switch
                    checked={dialogConnector.enabled}
                    onCheckedChange={(v) => toggleEnabled(dialogConnector.id, v)}
                  />
                </div>

                {/* Test Connection */}
                <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Connection Status</span>
                    {connStatus.status === "checking" && (
                      <Badge variant="outline" className="gap-1 text-xs font-normal">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Checking
                      </Badge>
                    )}
                    {connStatus.status === "connected" && (
                      <Badge variant="default" className="gap-1 text-xs font-normal bg-green-600 hover:bg-green-600/80">
                        <CircleCheck className="h-3 w-3" />
                        Connected
                        {connStatus.latency_ms != null && (
                          <span className="ml-0.5 opacity-75">{connStatus.latency_ms}ms</span>
                        )}
                      </Badge>
                    )}
                    {connStatus.status === "failed" && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="destructive" className="gap-1 text-xs font-normal">
                              <CircleX className="h-3 w-3" />
                              Failed
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {connStatus.message || "Connection failed"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testConnection(dialogConnector.id)}
                    disabled={connStatus.status === "checking"}
                  >
                    <Plug className="h-3.5 w-3.5 mr-1.5" />
                    Test
                  </Button>
                </div>

                {/* Readonly View */}
                {!editMode ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Connection Information</span>
                      <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Edit
                      </Button>
                    </div>
                    <div className="rounded-lg border border-border divide-y divide-border">
                      {connectFormFields ? (
                        connectFormFields
                          .filter((f) => {
                            if (f.name === "useConnectionString") return false;
                            if (f.showWhen) return dynamicFormValues[f.showWhen.field] === f.showWhen.value;
                            if (f.hideWhen) return dynamicFormValues[f.hideWhen.field] !== f.hideWhen.value;
                            return true;
                          })
                          .map((f) => (
                            <div key={f.name} className="flex items-center justify-between px-4 py-2.5">
                              <span className="text-sm text-muted-foreground">{f.label}</span>
                              <span className="text-sm font-medium font-mono truncate max-w-[280px]">
                                {f.type === "password"
                                  ? "••••••••"
                                  : f.type === "switch"
                                    ? (dynamicFormValues[f.name] ? "Enabled" : "Disabled")
                                    : (dynamicFormValues[f.name] as string) || "—"}
                              </span>
                            </div>
                          ))
                      ) : (
                        <div className="flex items-center gap-2 py-4 px-4 justify-center text-muted-foreground text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading...
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Edit Mode Form */
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Edit Connection</span>
                      <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>
                        Cancel
                      </Button>
                    </div>
                    {connectFormFields ? (
                      <ConnectFormRenderer
                        fields={connectFormFields}
                        values={dynamicFormValues}
                        onChange={(name, value) => setDynamicFormValues((prev) => ({ ...prev, [name]: value }))}
                        isEdit
                      />
                    ) : (
                      <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading form...
                      </div>
                    )}

                    <Button
                      className="w-full"
                      onClick={() => {
                        handleSaveConnection().then(() => setEditMode(false));
                      }}
                      disabled={saving || !connectFormFields}
                    >
                      {saving ? "Saving…" : "Save Changes"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Tables Tab */}
            {dialogTab === "tables" && dialogConnector && (
              <div className="space-y-3 py-2">
                {schemaLoading ? (
                  <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Connecting and fetching schema…</span>
                  </div>
                ) : schema.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <Database className="h-10 w-10 text-muted-foreground" />
                    <p className="text-muted-foreground">No tables found in this database</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        {selectedCount} table{selectedCount !== 1 ? "s" : ""} · {totalColumnsSelected} column
                        {totalColumnsSelected !== 1 ? "s" : ""} selected
                      </p>
                      <Button variant="outline" size="sm" onClick={allSelected ? deselectAll : selectAll}>
                        {allSelected ? "Deselect All" : "Select All"}
                      </Button>
                    </div>

                    <div className="space-y-1">
                      {schema.map((table) => {
                        const tSelected = isTableSelected(table.table_name);
                        const selectedCols = selections[table.table_name] || [];
                        const allColsSelected = tSelected && selectedCols.length === table.columns.length;
                        const someColsSelected = selectedCols.length > 0 && selectedCols.length < table.columns.length;
                        const isExpanded = expandedTables.has(table.table_name);

                        return (
                          <Collapsible
                            key={table.table_name}
                            open={isExpanded}
                            onOpenChange={() => toggleExpandTable(table.table_name)}
                          >
                            <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2 hover:bg-accent/50 transition-colors">
                              <Checkbox
                                checked={allColsSelected ? true : someColsSelected ? "indeterminate" : false}
                                onCheckedChange={() => toggleTable(table.table_name, table.columns)}
                              />
                              <CollapsibleTrigger asChild>
                                <button className="flex flex-1 items-center gap-2 text-sm font-medium text-left">
                                  <Table2 className="h-3.5 w-3.5 text-muted-foreground" />
                                  {table.table_name}
                                  <span className="text-xs text-muted-foreground font-normal">
                                    ({table.columns.length} col{table.columns.length !== 1 ? "s" : ""})
                                  </span>
                                  <ChevronDown
                                    className={cn(
                                      "ml-auto h-3 w-3 transition-transform text-muted-foreground",
                                      isExpanded && "rotate-180",
                                    )}
                                  />
                                </button>
                              </CollapsibleTrigger>
                            </div>
                            <CollapsibleContent>
                              <div className="ml-8 border-l border-border pl-3 py-1 space-y-0.5">
                                {table.columns.map((col) => {
                                  const isOffset = offsetColumns[table.table_name] === col.column_name;
                                  const desc = columnDescriptions[table.table_name]?.[col.column_name] || "";
                                  return (
                                    <div
                                      key={col.column_name}
                                      className="rounded px-2 py-1 text-sm hover:bg-accent/30 transition-colors space-y-1"
                                    >
                                      <div className="flex items-center gap-2">
                                        <Checkbox
                                          checked={isColumnSelected(table.table_name, col.column_name)}
                                          onCheckedChange={() => toggleColumn(table.table_name, col.column_name)}
                                        />
                                        <span className="flex-1">{col.column_name}</span>
                                        <span className="text-xs text-muted-foreground">{col.data_type}</span>
                                        {col.is_nullable === "YES" && (
                                          <span className="text-xs text-muted-foreground italic">nullable</span>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOffsetColumns((prev) => {
                                              const next = { ...prev };
                                              if (next[table.table_name] === col.column_name)
                                                delete next[table.table_name];
                                              else next[table.table_name] = col.column_name;
                                              return next;
                                            });
                                          }}
                                          className={cn(
                                            "text-[10px] px-1.5 py-0.5 rounded border transition-colors shrink-0",
                                            isOffset
                                              ? "bg-primary text-primary-foreground border-primary"
                                              : "border-border text-muted-foreground hover:border-primary/50",
                                          )}
                                          title={isOffset ? "Remove as offset column" : "Set as offset column"}
                                        >
                                          {isOffset ? "Offset ✓" : "Offset"}
                                        </button>
                                      </div>
                                      <div className="ml-6">
                                        <Input
                                          placeholder="Add column description..."
                                          className="h-7 text-xs"
                                          value={desc}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setColumnDescriptions((prev) => {
                                              const next = { ...prev };
                                              if (!next[table.table_name]) next[table.table_name] = {};
                                              next[table.table_name] = {
                                                ...next[table.table_name],
                                                [col.column_name]: val,
                                              };
                                              return next;
                                            });
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Per-table connect type & Schedule Syncing button */}
                              <div className="ml-8 pl-3 pt-2 pb-1 space-y-2">
                                {tSelected && allowRemote && (
                                  <div className="flex items-center gap-2">
                                    <Label className="text-xs text-muted-foreground shrink-0">Connect Type</Label>
                                    <Select
                                      value={connectTypes[table.table_name] || "sync"}
                                      onValueChange={(val) =>
                                        setConnectTypes((prev) => ({ ...prev, [table.table_name]: val }))
                                      }
                                    >
                                      <SelectTrigger className="h-7 w-[120px] text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="sync">Sync</SelectItem>
                                        <SelectItem value="remote">Remote</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                                {tSelected && (
                                  <Button
                                    size="sm"
                                    variant={offsetColumns[table.table_name] ? "default" : "outline"}
                                    disabled={savingSchema === table.table_name}
                                    onClick={() => scheduleSyncForTable(table.table_name)}
                                  >
                                    {savingSchema === table.table_name ? "Scheduling…" : "Schedule Syncing"}
                                  </Button>
                                )}
                                {tSelected && !offsetColumns[table.table_name] && (
                                  <p className="text-xs text-destructive mt-1">Select an offset column first</p>
                                )}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Connector Dialog */}
      <Dialog open={addConnOpen} onOpenChange={setAddConnOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <PipelineLogo logo={DB_TYPE_LOGO[addConnDbType]?.logo || null} logoColor={DB_TYPE_LOGO[addConnDbType]?.color || null} size="md" />
              <div>
                <div>New {DB_TYPE_LABELS[addConnDbType]?.label || addConnDbType} Connection</div>
                <p className="text-xs text-muted-foreground font-normal mt-0.5">Add a new connector to start syncing data</p>
              </div>
            </DialogTitle>
            <DialogDescription className="sr-only">
              Add a new {DB_TYPE_LABELS[addConnDbType]?.label || addConnDbType} database connection
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {connectFormFields ? (
              <ConnectFormRenderer
                fields={connectFormFields}
                values={dynamicFormValues}
                onChange={(name, value) => setDynamicFormValues((prev) => ({ ...prev, [name]: value }))}
              />
            ) : (
              <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading form...
              </div>
            )}
            <Button
              className="w-full"
              onClick={handleAddNewConnector}
              disabled={!(dynamicFormValues.name as string)?.trim() || addConnSaving || !connectFormFields}
            >
              {addConnSaving ? "Saving…" : "Add Connection"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* All Connectors Dialog */}
      <Dialog
        open={allConnectorsOpen}
        onOpenChange={(open) => {
          setAllConnectorsOpen(open);
          if (!open) {
            setPopupConnector(null);
            fetchConnectors();
            fetchJobs();
          }
        }}
      >
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-0">
            <DialogTitle className="font-mono text-sm tracking-wider">
              {popupConnector
                ? (CONNECTOR_ITEMS.find((c) => c.id === popupConnector)?.label ?? popupConnector).toUpperCase()
                : "ALL DATA SOURCES"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto">
            {allConnectorsOpen && !popupConnector && (
              <AllConnectors onSelectConnector={(id) => setPopupConnector(id)} />
            )}
            {allConnectorsOpen &&
              popupConnector &&
              (() => {
                const item = CONNECTOR_ITEMS.find((c) => c.id === popupConnector);
                return (
                  <>
                    <div className="flex items-center gap-2 border-b border-border bg-card/50 px-4 py-2.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPopupConnector(null)}
                        className="gap-1.5 text-xs font-mono"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        All Connectors
                      </Button>
                      <span className="text-muted-foreground text-xs">/</span>
                      <span className="text-sm font-medium">{item?.label ?? popupConnector}</span>
                    </div>
                    <div className="flex-1 min-h-0 overflow-auto">
                      {popupConnector === "ds-manual-uploads" ? (
                        <ManualUploads />
                      ) : popupConnector === "ds-gmail" ? (
                        <GmailConnector />
                      ) : popupConnector === "ds-google-drive" ? (
                        <GoogleDriveConnector />
                      ) : popupConnector === "ds-scripts" ? (
                        <ScriptsManager />
                      ) : popupConnector === "ds-sql-db" ? (
                        <DatabaseConnections />
                      ) : item?.dbType ? (
                        <GenericConnectorPage key={item.id} label={item.label} dbType={item.dbType} />
                      ) : null}
                    </div>
                  </>
                );
              })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SyncingMonitor;
